<template>
  <div class="q-mt-md">
    <q-expansion-item dense class="cus-expansion" default-opened>
      <template v-slot:header>
        <q-item-section class="condition-setting-title"> 選択されたオーディエンスサイズ </q-item-section>

        <q-item-section side>
          <span class="text-h6 text-bold">{{ audienceSize }}</span>
        </q-item-section>
      </template>
      <q-table
        v-model:pagination="pagination"
        :rows-per-page-options="rowPerPageOptions"
        @request="onRequest"
        :rows="rows"
        :no-data-label="$t('messages.no_data_available')"
        row-key="user_id"
        flat
        dense
        bordered
        virtual-scroll
        separator="cell"
        class="text-grey-8"
        :style="rows && rows.length ? 'height: 390px' : ''"
        title="オーディエンスリスト"
        ref="audienceFilterRef"
      >
        <template v-slot:header="props">
          <q-tr :props="props">
            <q-th>ユーザー名</q-th>
            <q-th>追加日</q-th>
          </q-tr>
        </template>
        <template v-slot:body="props">
          <q-tr :props="props">
            <q-td>
              <span
                class="cursor-pointer hyper-link-chart"
                @click="onOpenActivityModal(props.row.user_id, props.row.tracked_from, props.row.tracked_to)"
              >
                {{ props.row.user_name }}
              </span>
            </q-td>
            <q-td>{{ props.row.created_at }}</q-td>
          </q-tr>
        </template>
        <template v-slot:no-data>
          <div class="col no-data-wrapper text-subtitle2">
            {{ $t('label.audience.no_data_message') }}
          </div>
        </template>
      </q-table>
    </q-expansion-item>
  </div>

  <div class="q-mt-md">
    <q-expansion-item dense class="cus-expansion" default-opened>
      <template v-slot:header>
        <q-item-section class="condition-setting-title"> 条件設定 </q-item-section>
        <q-item-section side>
          <span class="text-h6 text-bold">{{ audienceSize }}</span>
        </q-item-section>
      </template>
      <ConditionOr v-model="value.conditions" v-if="value.conditions" />
    </q-expansion-item>
  </div>
</template>

<script lang="ts">
import { mixins, Options } from 'vue-class-component'
import { Prop, Watch } from 'vue-property-decorator'
import { maska } from 'maska'
import DateRangeSelectorV2 from '@/components/common/ui/DateRangeSelectorV2.vue'
import { IAudience, IAudienceQuestionRow } from '@/utils/types'
import AudienceMixin from '@/components/audience/mixins/AudienceMixin.vue'
import { USER_ANSWER } from '@/store/actions'
import { constant } from '@/utils/constants'
import ConditionOr from '@/components/common/condition-setting/ConditionOr.vue'
@Options({
  components: { AudienceMixin, DateRangeSelectorV2, ConditionOr },
  directives: { maska },
  emits: ['update:modelValue', 'update:handleLoading', 'openActivityModal'],
})
export default class AudienceFilter extends mixins(AudienceMixin) {
  @Prop()
  modelValue!: IAudience

  @Prop()
  loading!: boolean

  rows: IAudienceQuestionRow[] = []
  audienceSize = 0
  isTestMode = false

  pagination = {
    sortBy: '',
    descending: false,
    page: 1,
    rowsPerPage: constant.ROW_PER_PAGE,
    rowsNumber: 10,
  }

  private conditionsDebounceTimer: ReturnType<typeof setTimeout> | null = null

  get rowPerPageOptions() {
    return constant.ROW_PER_PAGE_OPTIONS
  }

  get value() {
    return this.modelValue
  }

  set value(value: IAudience) {
    this.$emit('update:modelValue', value)
  }

  async loadDataFromServer() {
    // get initial data from server (1st page)
    await this.$refs.audienceFilterRef.requestServerInteraction()
  }

  @Watch('value.is_test_mode', { deep: true })
  async onIsTestModeChange(newVal: any, oldVal: any) {
    console.log('onIsTestModeChange triggered', { newVal, oldVal })
    if (this.loading) {
      return
    }
    await this.loadDataFromServer()
  }

  // Watch conditions using computed string for reliable change detection
  get conditionsString() {
    return JSON.stringify(this.modelValue?.conditions || [])
  }

  // Watch conditionsString for reliable change detection
  @Watch('conditionsString')
  async onConditionsStringChange(newVal: string, oldVal: string) {
    console.log('onConditionsStringChange watch triggered', { newVal, oldVal })

    if (!oldVal || oldVal === '') {
      return // Skip initial watch
    }

    // Clear any existing debounce timer
    if (this.conditionsDebounceTimer) {
      clearTimeout(this.conditionsDebounceTimer)
    }

    // Debounce the API call to avoid calling it on every keystroke
    this.conditionsDebounceTimer = setTimeout(async () => {
      console.log('onConditionsStringChange triggered (debounced)', {
        newVal,
        oldVal,
      })

      if (this.loading) {
        console.log('Skipping API call - already loading')
        return
      }

      // Check if conditions actually changed
      if (newVal !== oldVal) {
        console.log('Conditions changed, calling loadDataFromServer')
        await this.loadDataFromServer()
      } else {
        console.log('Conditions unchanged, skipping API call')
      }

      this.conditionsDebounceTimer = null
    }, 600) // Slightly longer than the input debounce (500ms) to ensure it fires after the final value
  }

  async onRequest(props) {
    const { page, rowsPerPage, sortBy, descending } = props.pagination
    const filter = props.filter
    this.$emit('update:handleLoading', true)
    console.log('onRequest start')

    const fetchCount = rowsPerPage === 0 ? this.audienceSize : rowsPerPage
    const startRow = (page - 1) * rowsPerPage
    const returnedData: [] = await this.fetchFromServer(startRow, fetchCount, filter, sortBy, descending)
    // eslint-disable-next-line
    this.rows = returnedData['rows']
    console.log('onRequest rows', this.rows)
    if (!this.rows) {
      this.rows = []
    }
    // eslint-disable-next-line
    this.audienceSize = returnedData['audience_size']
    // eslint-disable-next-line
    this.pagination.rowsNumber = returnedData['total_rows']
    this.rows.splice(0, this.rows.length, ...this.rows)
    this.pagination.page = page
    this.pagination.rowsPerPage = rowsPerPage
    this.pagination.sortBy = sortBy
    this.pagination.descending = descending
    console.log('onRequest end')

    this.$emit('update:handleLoading', false)
  }

  async fetchFromServer(startRow, count, filter, sortBy, descending) {
    const items = await this.$store.dispatch(USER_ANSWER.USER_ANSWER_ROWS_V2, {
      audience: this.value,
      is_test_mode: this.isTestMode,
      filter: {
        start_row: startRow,
        rows_number: count,
        filter: filter,
        sort_by: sortBy,
        descending: descending,
      },
    })

    return items
  }

  validate() {
    console.log('validate', this.value.conditions)
    // Check if at least one condition has a type selected
    if (!this.value.conditions || this.value.conditions.length === 0) {
      return false
    }

    // Check if there's at least one condition setting with a type selected
    // Note: conditionOr.conditions can be IAudienceBuilderConditionSetting[] directly
    for (const conditionOr of this.value.conditions) {
      if (conditionOr.conditions && conditionOr.conditions.length > 0) {
        for (const item of conditionOr.conditions) {
          // Check if item has 'type' property (it's a conditionSetting)
          if ((item as any).type && (item as any).type.trim() !== '') {
            return true
          }
          // Check if item has nested 'conditions' property (it's a conditionAnd)
          if ((item as any).conditions && Array.isArray((item as any).conditions)) {
            for (const nestedSetting of (item as any).conditions) {
              if (nestedSetting.type && nestedSetting.type.trim() !== '') {
                return true
              }
            }
          }
        }
      }
    }

    return false
  }

  onOpenActivityModal(userId, from, to) {
    this.$emit('openActivityModal', userId, from, to)
  }

  beforeUnmount() {
    // Clean up debounce timer on component destroy
    if (this.conditionsDebounceTimer) {
      clearTimeout(this.conditionsDebounceTimer)
    }
  }
}
</script>
<style scoped lang="scss">
:deep(.cus-expansion .q-item) {
  padding-left: 0;
  padding-right: 0;
}

.condition-setting-title {
  font-weight: 700;
  font-size: 18px;
  line-height: 140%;
  letter-spacing: 0.02em;
  vertical-align: middle;
}

:deep(.q-table thead tr th) {
  font-weight: bold;
  text-align: left;
}

.no-data-wrapper {
  height: 305px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hyper-link-chart {
  color: #7491BD;
  text-decoration: underline;
}
</style>
